<template>
    <l-map
        ref="myMap"
        :crs="crs"
        :min-zoom="minZoom"
        :max-zoom="maxZoom"
        :max-bounds="[[0, 0], [scene.size.y, scene.size.x]]"
        style="background-color: white"
        :options="{attributionControl: false, zoomAnimation: false}"
        @update:zoom="setZoom"
        @click="onMapClick"
    >
        <l-image-overlay
            :url="scene.imageUrl"
            :bounds="[[0, 0], [scene.size.y, scene.size.x]]"
        />

        <l-circle
            v-for="device in emitterDevices.filter(d => d.actionRadiusMeters)"
            :key="'emitter-area-' + device.id"
            :lat-lng="[device.posY, device.posX]"
            :radius="(device.actionRadiusMeters ?? 0) * scene.unitsPerMeter"
            :fill-color="'#ff2600'"
            :fill-opacity="device.isEmitting ? 0.2 : 0"
            :weight="1"
            :color="device.isEmitting ? 'rgba(179, 90, 77, 0.4)' : 'rgba(0, 0, 0, 0.4)'"
        />

        <l-circle
            v-for="device in detectorDevices.filter(d => d.actionRadiusMeters && !isDetectorActive(d))"
            :key="'detector-area-passive-' + device.id"
            :lat-lng="[device.posY, device.posX]"
            :radius="(device.actionRadiusMeters ?? 0) * scene.unitsPerMeter"
            :fill-opacity="0"
            :weight="6"
            :color="'rgba(0, 0, 0, 0.2)'"
            :dash-array="'0, 15'"
        />

        <l-circle
            v-for="device in detectorDevices.filter(d => d.actionRadiusMeters && isDetectorActive(d))"
            :key="'detector-area-active-' + device.id"
            :class-name="'rotated-circle'"
            :lat-lng="[device.posY, device.posX]"
            :radius="(device.actionRadiusMeters ?? 0) * scene.unitsPerMeter"
            :fill-opacity="0"
            :weight="6"
            :color="'rgba(214,14,44,0.6)'"
            :dash-array="'0, 15'"
        />

        <l-circle
            v-for="device in detectorDevices.filter(d => d.actionRadiusMeters && isDetectorActive(d))"
            :key="'detector-area2-' + device.id"
            :class-name="'flashing-circle'"
            :lat-lng="[device.posY, device.posX]"
            :radius="(device.actionRadiusMeters ?? 0) * scene.unitsPerMeter"
            :fill-color="'#ff1f2e'"
            :fill-opacity="0.5"
            :weight="0"
        />

        <DeviceMarker
            v-for="(device, i) in realDevices" :key="device.id"
            :device="device"
            :force-latlng="getLatlng(device)"
            :offset="getOffset(device)"
            :is-compact="false"
            :show-names="zoom >= detailsZoom"
            :animation-delay="i / realDevices.length"
        />

        <l-marker
            v-if="mode === 'picker'"
            :lat-lng="pickerCoords"
            draggable
            @dragend="onPickerDragEnd"
        >
            <l-icon :icon-anchor="[12, 12]">
                <div class="crosshair">
                    <div class="crosshair-1" />
                    <div class="crosshair-2" />
                    <div class="crosshair-3" />
                    <div class="crosshair-4" />
                </div>
            </l-icon>
        </l-marker>
    </l-map>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import Vue from 'vue'
import {Prop, Watch} from 'vue-property-decorator'
import {Scene} from '@/types/Scene'
import {CRS, Map} from 'leaflet'
import _ from 'lodash'
import {Device, isDeviceDetector, isDeviceEmitter} from '@/models/Device'
import DeviceMarker from './DeviceMarker.vue'
import {Coords} from '@/types/Coords'
import {STATUS_ALERT} from '@/types/device/DeviceStatus'

interface LatLngObject
{
    lat: number
    lng: number
}

interface DragEvent
{
    target: {
        _latlng: LatLngObject
    }
}

interface MapClickEvent
{
    latlng: LatLngObject
}

@Component({
    components: {DeviceMarker},
})
export default class Scheme extends Vue
{
    @Prop({type: Object, required: true})
    scene!: Scene

    @Prop({type: Array, default: () => []})
    devices!: Device[]

    @Prop({type: Object, default: () => null})
    focusedDevice!: Device | null

    @Prop({type: String, default: 'devices'})
    mode!: 'devices' | 'picker'

    @Prop({type: Array, default: () => [0, 0]})
    pickerCoords!: [number, number]

    realDevices: Device[] = []

    crs = CRS.Simple

    zoom = 0
    minZoom = -3
    maxZoom = 2

    get emitterDevices(): Device[] {
        return this.realDevices.filter(isDeviceEmitter)
    }

    get detectorDevices(): Device[] {
        return this.realDevices.filter(isDeviceDetector)
    }

    @Watch('scene', {deep: true, immediate: true})
    onSceneChanged(value?: Scene, oldValue?: Scene) {
        if (value && !_.isEqual(value, oldValue)) {
            this.$nextTick(() => {
                this.map.fitBounds([[0, 0], [this.scene.size.y, this.scene.size.x]])
            })
        }
    }

    @Watch('devices', {deep: true, immediate: true})
    onDevicesChanged(value: Device[]) {
        if (value && !_.isEqual(value, this.realDevices)) {
            this.realDevices = value
        }
    }

    get detailsZoom() {
        return Math.round((this.maxZoom + this.minZoom) / 2) - 1
    }

    get focusZoom() {
        return this.detailsZoom + 2
    }

    get map(): Map {
        return (this.$refs.myMap as unknown as { mapObject: Map }).mapObject
    }

    @Watch('focusedDevice', {deep: true, immediate: true})
    onFocusedDeviceChanged(device: Device) {
        if (!device) {
            return
        }

        this.$nextTick(() => {
            if (device.posY != null && device.posX != null) {
                this.map.flyTo([device.posY, device.posX], this.focusZoom, {
                    animate: true,
                    duration: 0.6,
                })
            }
            this.$emit('change:focused-device', null)
        })
    }

    onMapClick(e: MapClickEvent) {
        if (this.mode === 'picker') {
            this.setPickerCoords([e.latlng.lat, e.latlng.lng])
        }
    }

    onPickerDragEnd(e: DragEvent) {
        this.setPickerCoords([e.target._latlng.lat, e.target._latlng.lng])
    }

    setPickerCoords(coords: Coords) {
        this.$emit('update:picker-coords', coords)
    }

    setZoom(zoom: number) {
        this.zoom = zoom
    }

    isDetectorActive(device: Device) {
        return isDeviceDetector(device) && device.status === STATUS_ALERT
    }
    
    get latlngGroups() {
        const groups: {latlng: [number, number], devices: Device[]}[] = []
        _.sortBy(this.realDevices, isDeviceDetector).forEach((device: Device) => {
            for (let group of groups) {
                const distance = Math.sqrt(
                    Math.pow(group.latlng[0] - device.posY, 2) +
                    Math.pow(group.latlng[1] - device.posX, 2)
                )
                if (distance < 100) {
                    group.devices.push(device)
                    return
                }
            }

            groups.push({latlng: [device.posY, device.posX], devices: [device]})
        })

        return groups
    }

    getLatlng(device: Device): [number, number] | undefined {
        const group = this.latlngGroups.find(group => group.devices.includes(device))
        return group?.latlng
    }

    getOffset(device: Device): number | undefined {
        const group = this.latlngGroups.find(group => group.devices.includes(device))
        return group ? group.devices.indexOf(device) : undefined
    }
}
</script>
